import { ApiResult, HttpClient } from "../http.js";
import type {
  SupplierGrantDto,
  SupplierOrgDto,
  SupplierPermissionsDto,
} from "../types/supplier-access.js";

export interface InviteAccountManagerPayload {
  email: string;
  supplierCompanyId?: string | null;
  message?: string | null;
}

/**
 * Customer side of supplier access (`/api/supplier-access`): the active org is the CUSTOMER.
 * Everything needs `settings.manageMembers`; the permissions pair needs `settings.managePermissions`.
 * An older backend answers 404 for the whole controller, which callers treat as "not available".
 */
export class SupplierAccessApi {
  constructor(private readonly http: HttpClient) {}

  /** Every grant on the active company, newest first. */
  list(): Promise<ApiResult<SupplierGrantDto[]>> {
    return this.http.get<SupplierGrantDto[]>("/api/supplier-access");
  }

  /** Supplier-plan companies the invite form can offer. */
  listSuppliers(): Promise<ApiResult<SupplierOrgDto[]>> {
    return this.http.get<SupplierOrgDto[]>("/api/supplier-access/suppliers");
  }

  /** Invite an account manager by e-mail; resolves to the new grant id. */
  invite(payload: InviteAccountManagerPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/supplier-access/invitations", { body: payload });
  }

  /** Approve a supplier's request for access. */
  approve(id: string): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/supplier-access/${encodeURIComponent(id)}/approve`);
  }

  /** Decline a supplier's request for access. */
  decline(id: string): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/supplier-access/${encodeURIComponent(id)}/decline`);
  }

  /** Remove an active grant, or cancel a pending invitation. Takes effect on the next request. */
  revoke(id: string, reason?: string): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/supplier-access/${encodeURIComponent(id)}/revoke`, {
      body: { reason: reason ?? null },
    });
  }

  getPermissions(): Promise<ApiResult<SupplierPermissionsDto>> {
    return this.http.get<SupplierPermissionsDto>("/api/supplier-access/permissions");
  }

  /** Replace the chosen set (must stay within the ceiling). An empty list resets to the defaults. */
  setPermissions(permissions: string[]): Promise<ApiResult<SupplierPermissionsDto>> {
    return this.http.put<SupplierPermissionsDto>("/api/supplier-access/permissions", {
      body: { permissions },
    });
  }
}
